import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';

gsap.registerPlugin(ScrollTrigger);

const DESKTOP_MEDIA_QUERY = '(min-width: 1025px) and (hover: hover) and (pointer: fine)';
const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
const DEFAULT_SHIFT = 50;
const VISIBILITY_MARGIN = '150px 0px';
const RESIZE_DELAY = 120;
const initializedHeroes = new WeakSet();
const activeControllers = new Set();
let resizeTimeout;
let resizeFrame;

const getPositionValue = (value) => {
  const normalizedValue = String(value || '').trim().toLowerCase();

  if (normalizedValue === 'top') {
    return 0;
  }

  if (normalizedValue === 'bottom') {
    return 100;
  }

  if (normalizedValue === 'center') {
    return 50;
  }

  const parsedValue = Number.parseFloat(normalizedValue);

  return normalizedValue.endsWith('%') && Number.isFinite(parsedValue) ? parsedValue : 50;
};

const getVerticalObjectPosition = (image) => {
  const values = window.getComputedStyle(image).objectPosition.trim().split(/\s+/);

  return getPositionValue(values[1] || 'center') / 100;
};

const waitForImage = (image) => new Promise((resolve, reject) => {
  const decodeImage = () => {
    if (typeof image.decode !== 'function') {
      resolve();
      return;
    }

    image.decode().then(resolve).catch(reject);
  };

  if (image.complete) {
    if (image.naturalWidth) {
      decodeImage();
    } else {
      reject();
    }

    return;
  }

  image.addEventListener('load', decodeImage, { once: true });
  image.addEventListener('error', reject, { once: true });
});

const scheduleActiveControllersRefresh = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    resizeTimeout = undefined;

    if (resizeFrame !== undefined) {
      cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = undefined;
      activeControllers.forEach((controller) => controller.refresh());
      ScrollTrigger.refresh();
    });
  }, RESIZE_DELAY);
};

window.addEventListener('resize', scheduleActiveControllersRefresh, { passive: true });

const initHeroParallax = (scope = document) => {
  const heroes = scope.matches?.('[data-hero-parallax]')
    ? [scope]
    : scope.querySelectorAll('[data-hero-parallax]');

  heroes.forEach((hero) => {
    if (initializedHeroes.has(hero)) {
      return;
    }

    const image = hero.querySelector('img');
    const layer = hero.querySelector('picture');

    if (!image || !layer) {
      return;
    }

    const shift = Number.parseFloat(hero.dataset.heroParallaxShift) || DEFAULT_SHIFT;
    const direction = Number.parseFloat(hero.dataset.heroParallaxDirection) < 0 ? -1 : 1;
    const missionSection = hero.closest('.mission-sec');
    const trigger = missionSection || hero;
    const media = gsap.matchMedia();

    media.add({
      desktop: DESKTOP_MEDIA_QUERY,
      reducedMotion: REDUCED_MOTION_MEDIA_QUERY,
    }, (context) => {
      if (!context.conditions.desktop || context.conditions.reducedMotion) {
        return undefined;
      }

      let isActive = true;
      let isVisible = false;
      let isReady = false;
      let isTriggerEnabled = false;
      let tween;
      let observer;

      const resetLayer = () => {
        hero.classList.remove('is-hero-parallax-ready');
        layer.style.removeProperty('--hero-parallax-layer-height');
        layer.style.removeProperty('--hero-parallax-layer-top');
        gsap.set(layer, { clearProps: 'transform,transformOrigin,willChange' });
      };

      const pauseTrigger = () => {
        if (!tween || !isTriggerEnabled) {
          return;
        }

        tween.scrollTrigger.disable(false, true);
        tween.scrollTrigger.getTween()?.pause();
        layer.style.removeProperty('will-change');
        isTriggerEnabled = false;
      };

      const activateTrigger = () => {
        if (!tween || !isVisible || isTriggerEnabled) {
          return;
        }

        layer.style.setProperty('will-change', 'transform');
        tween.scrollTrigger.enable(false, true);
        tween.scrollTrigger.getTween()?.play();
        tween.scrollTrigger.update();
        isTriggerEnabled = true;
      };

      const createTween = () => {
        tween?.scrollTrigger?.kill();
        tween?.kill();
        resetLayer();

        const heroHeight = hero.clientHeight;
        const heroWidth = hero.clientWidth;
        const renderedHeight = image.naturalWidth
          ? heroWidth * (image.naturalHeight / image.naturalWidth)
          : 0;
        const focusY = getVerticalObjectPosition(image);
        const motion = -direction;
        const availableBleed = Math.max(0, renderedHeight - heroHeight);
        const availableSide = motion > 0 ? focusY : 1 - focusY;
        const desiredTravel = heroHeight * (shift / 100);

        isTriggerEnabled = false;

        if (!heroHeight || !heroWidth || !availableBleed || !availableSide) {
          return;
        }

        const bleed = Math.min(availableBleed, desiredTravel / availableSide);
        const travel = bleed * availableSide;

        hero.classList.add('is-hero-parallax-ready');
        layer.style.setProperty('--hero-parallax-layer-height', `${heroHeight + bleed}px`);
        layer.style.setProperty('--hero-parallax-layer-top', `${-bleed * focusY}px`);
        gsap.set(layer, { transformOrigin: 'center center' });

        tween = gsap.fromTo(layer, {
          y: 0,
        }, {
          y: motion * travel,
          ease: 'none',
          force3D: true,
          scrollTrigger: {
            trigger,
            start: missionSection ? 'top 80%' : 'top top',
            end: missionSection ? 'bottom 20%' : 'bottom top',
            scrub: 0.2,
          },
        });

        if (isVisible) {
          activateTrigger();
        } else {
          tween.scrollTrigger.disable(false, true);
        }
      };

      const controller = {
        refresh() {
          if (isActive && isReady) {
            createTween();
          }
        },
      };

      activeControllers.add(controller);

      if (typeof IntersectionObserver === 'function') {
        observer = new IntersectionObserver((entries) => {
          const entry = entries.find((item) => item.target === trigger);

          if (!entry) {
            return;
          }

          isVisible = entry.isIntersecting;

          if (isVisible) {
            activateTrigger();
          } else {
            pauseTrigger();
          }
        }, {
          rootMargin: VISIBILITY_MARGIN,
        });
        observer.observe(trigger);
      } else {
        isVisible = true;
      }

      waitForImage(image).then(() => {
        if (!isActive) {
          return;
        }

        isReady = true;
        createTween();
        ScrollTrigger.refresh();
      }).catch(() => {});

      return () => {
        isActive = false;
        activeControllers.delete(controller);
        observer?.disconnect();
        tween?.scrollTrigger?.kill();
        tween?.kill();
        resetLayer();
      };
    });

    initializedHeroes.add(hero);
  });
};

initHeroParallax();
window.initHeroParallax = initHeroParallax;
